// Create a new file: /components/workflow/StructuralConditionalNodeModal.jsx

import React, { useState, useMemo } from 'react';
import { Link2, Network } from 'lucide-react';

import SchemaViewer from '../NewWorkflow/shared/SchemaViewer'; // Adjust path if needed
import {
  fieldsToSchema,
  schemaToFields,
  flattenSchemaToFields,
} from '../NewWorkflow/utils/schemaUtils';

const StructuralConditionalNodeModal = ({
  mode,
  node,
  nodes,
  edges,
  onSave,
  onDelete,
  onClose,
  globalState,
}) => {
  const [selectedConditionPath, setSelectedConditionPath] = useState(
    node.data.selectedConditionPath || null,
  );

  const inputRootFields = useMemo(() => {
    const connectedInputNodes = edges
      .filter((edge) => edge.target === node.id)
      .map((edge) => nodes.find((n) => n.id === edge.source))
      .filter(Boolean)
      .filter((parentNode) => parentNode.type !== 'loopBreak') // ⭐ ADD THIS LINE
      .map((parentNode) => {
        let schema;
        if (
          parentNode.data.jsonOutput &&
          parentNode.data.jsonOutputSchema?.properties
        ) {
          schema = parentNode.data.jsonOutputSchema;
        } else {
          schema = {
            title: 'Simple Output',
            type: 'object',
            properties: {
              output: {
                type: 'string',
                description: `Output from "${parentNode.data.label || parentNode.type}"`,
              },
            },
          };
        }
        return {
          id: `input_${parentNode.id}`,
          fieldName: parentNode.data.label || 'Input Node',
          dataType: 'Object',
          children: schemaToFields(schema, ''),
        };
      })
      .filter(Boolean);

    // ⭐ 2. Create a "virtual node" for the Global State if it exists
    const globalStateNode = [];
    if (globalState && globalState.fields.length > 0) {
      const globalSchema = fieldsToSchema(globalState.fields, 'globalState');
      globalStateNode.push({
        id: 'globalState', // Special, reserved ID
        fieldName: 'Global State',
        dataType: 'Object',
        children: schemaToFields(globalSchema, ''),
      });
    }

    // ⭐ 3. Combine the Global State with the other input nodes
    return [...globalStateNode, ...connectedInputNodes];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, nodes, edges]);

  const allInputFields = useMemo(
    () => flattenSchemaToFields(inputRootFields),
    [inputRootFields],
  );

  const handleFieldClick = (path, field) => {
    setSelectedConditionPath(path);
  };

  // This function tells the SchemaViewer to only allow Boolean fields to be clickable
  const isFieldValid = (field) => field.dataType.toLowerCase() === 'boolean';

  // Auto-generate the output schema
  const { jsonOutputSchema, outputRootField } = useMemo(() => {
    const outputFields = [
      {
        id: 'condition',
        fieldName: 'condition',
        dataType: 'Boolean',
        children: [],
      },
    ];
    const schema = fieldsToSchema(outputFields);
    const rootFieldForViewer = [
      {
        id: 'root',
        fieldName: 'Output',
        dataType: 'Object',
        children: outputFields,
      },
    ];
    return { jsonOutputSchema: schema, outputRootField: rootFieldForViewer };
  }, []);

  const handleSaveAndClose = () => {
    onSave({
      ...node,
      data: {
        ...node.data,
        label: `If: ${selectedConditionPath?.split('.').pop() || ''}`,
        selectedConditionPath,
        jsonOutputSchema, // The simple { "condition": boolean } schema
        jsonOutput: true,
      },
    });
    onClose();
  };

  // --- NEW: Find the display name for the selected path ---
  const selectedFieldDisplayName = useMemo(() => {
    if (!selectedConditionPath) return null;
    const field = allInputFields.find((f) => f.path === selectedConditionPath);
    return field ? field.display : selectedConditionPath;
  }, [selectedConditionPath, allInputFields]);

  return (
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        className="flex h-[90vh] w-full max-w-7xl flex-col rounded-xl border border-slate-700 bg-slate-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 border-b border-slate-700 p-4">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-white">
            <Network /> Configure Structural Condition Node
          </h2>
        </div>
        <div className="grid flex-grow grid-cols-2 gap-4 overflow-hidden p-4">
          <div className="flex flex-col gap-2 overflow-y-auto pr-2">
            <h3 className="text-lg font-semibold text-slate-200">
              Select a Boolean Field
            </h3>
            <p className="text-sm text-slate-400">
              Only boolean fields are enabled. The workflow will branch based on
              this field's value.
            </p>
            <SchemaViewer
              rootFields={inputRootFields}
              onFieldClick={handleFieldClick}
              isValidField={isFieldValid}
              selectedPath={selectedConditionPath}
            />
          </div>
          <div className="flex flex-col gap-2 overflow-y-auto pl-2">
            <h3 className="text-lg font-semibold text-slate-200">
              Generated Output
            </h3>
            <p className="text-sm text-slate-400">
              For logging, the node will output an object with the resolved
              boolean value.
            </p>
            <SchemaViewer rootFields={outputRootField} />
            {/* --- NEW: VISUAL MAPPING INDICATOR --- */}
            {selectedFieldDisplayName && (
              <div className="mt-4 rounded-lg border border-zinc-700 bg-zinc-900/50 p-4">
                <h4 className="mb-2 text-sm font-semibold text-slate-200">
                  Mapping
                </h4>
                <div className="flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-2 truncate rounded-md bg-indigo-500/10 px-2 py-1 font-mono text-indigo-300">
                    <Link2 size={14} />
                    <span>{selectedFieldDisplayName}</span>
                  </div>
                  <span className="text-slate-400">→</span>
                  <div className="rounded-md bg-slate-700 px-2 py-1 font-mono text-slate-100">
                    condition
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center justify-between rounded-b-lg border-t border-zinc-700 bg-zinc-800/50 p-4">
          {mode === 'edit' && node?.id && (
            <button
              onClick={() => onDelete(node.id)}
              className="rounded-md bg-red-900/50 px-4 py-2 text-sm font-semibold text-red-400 transition-colors hover:bg-red-900/80"
            >
              Delete Node
            </button>
          )}
          <div className="flex-grow" />
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="rounded-md bg-zinc-700 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-zinc-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndClose}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-500"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StructuralConditionalNodeModal;
